import React from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { useNavigate } from 'react-router-dom'
import { useGetEnrollConfigQuery, useGetMyStudentsQuery } from '../../api/enrollment'
import { useLoader } from '../../components/Loader'
import { EnrollStudent } from '../../models/enrollment'
import { useAuth } from '../../providers/auth/AuthProvider'
import { ineligibleMessage } from '../../messages/eligibility'

const hasAssignedCourse = (student: EnrollStudent) =>
  Boolean(student.currentYearCourse && student.currentYearCourse !== '-')

// Cố tình không hiện tên lớp ở đây: tên lớp có quy luật màu riêng theo ngành
// (CourseChip), còn chip này nói về trạng thái ghi danh. Lớp chỉ hiện ở trang chi tiết.
const statusLabel = (student: EnrollStudent) => {
  if (student.registered) {
    return <Chip size="small" color="success" label="Đã ghi danh" />
  }
  if (student.pendingPaymentCode) {
    return <Chip size="small" color="warning" label="Chờ thanh toán" />
  }
  if (!student.eligible) {
    return null
  }
  return <Chip size="small" color="primary" label="Chưa ghi danh" />
}

const lastYearSummary = (student: EnrollStudent, enrollmentOpen?: boolean) => {
  if (!student.eligible) {
    return null
  }
  // Chỉ những em còn có thể ghi danh mới cần biết vì sao nút ghi danh không còn.
  // So sánh với false để lúc config chưa về thì chưa hiện gì.
  const missedEnrollment =
    enrollmentOpen === false && !student.registered && !student.pendingPaymentCode
  const promoted = student.passedLastYear
  const avg =
    typeof student.lastYearAverage === 'number'
      ? student.lastYearAverage.toFixed(1)
      : null
  return (
    <Stack spacing={0.5}>
      {student.lastYearCourseName && (
        <Typography component="div" variant="body2">
          Lớp cũ: <b>{student.lastYearCourseName}</b>{' '}
          <Box
            component="span"
            sx={{ color: promoted ? 'success.main' : 'warning.main', fontWeight: 600 }}
          >
            ({promoted ? 'lên lớp' : 'ở lại lớp'})
          </Box>
        </Typography>
      )}
      {!promoted && avg && (
        <Typography component="div" variant="body2" color="text.secondary">
          Điểm trung bình: <b>{avg}</b>
        </Typography>
      )}
      {!promoted && student.lastYearComment && (
        <Typography component="div" variant="body2" color="text.secondary">
          Nhận xét: {student.lastYearComment}
        </Typography>
      )}
      {student.nextProgramName && (
        <Typography component="div" variant="body2" color="text.secondary">
          {promoted ? 'Lớp tiếp theo' : 'Học lại'}: <b>{student.nextProgramName}</b>
        </Typography>
      )}
      {missedEnrollment && (
        <Alert severity="warning" sx={{ py: 0 }}>
          Đã kết thúc ghi danh năm học này do các lớp đã đủ số lượng.
        </Alert>
      )}
    </Stack>
  )
}

const GuardianHome: React.FC = () => {
  const navigate = useNavigate()
  const { logout } = useAuth()
  const { data: config } = useGetEnrollConfigQuery()
  const { data: students = [], isFetching, error } = useGetMyStudentsQuery()
  const { Loader } = useLoader()

  const enrollable = students.filter(
    (s) => !s.registered && s.eligible && !s.pendingPaymentCode
  )
  // Chờ config trả về mới hiện nút, tránh nhá nút rồi ẩn khi kỳ ghi danh đã đóng.
  const showEnrollButton = enrollable.length > 0 && config?.onlineEnrollmentOpen === true

  return (
    <Container maxWidth="sm" sx={{ py: 3 }}>
      <Loader loading={isFetching} />
      <Stack spacing={3}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" fontWeight={700}>
            Đoàn sinh của bạn
          </Typography>
          <Button size="small" onClick={() => logout()}>
            Đăng xuất
          </Button>
        </Stack>

        {error && (
          <Alert severity="error">
            Không tải được danh sách. Kiểm tra đăng nhập hoặc thử lại sau.
          </Alert>
        )}

        {students.length === 0 && !isFetching && (
          <Alert severity="warning">
            Không tìm thấy đoàn sinh khớp số điện thoại này. Vui lòng liên hệ giáo xứ để cập nhật thông tin.
          </Alert>
        )}

        <List sx={{ width: '100%' }}>
          {students.map((student) => {
            const clickable = hasAssignedCourse(student)
            const ineligibleText =
              !student.registered && !student.eligible
                ? ineligibleMessage(student.ineligibleCode, config?.onlineEnrollmentOpen)
                : null
            const itemContent = (
              <ListItemText
                sx={{ width: '100%', m: 0 }}
                primaryTypographyProps={{ component: 'div' }}
                secondaryTypographyProps={{ component: 'div' }}
                primary={
                  <Stack
                    direction="row"
                    alignItems="flex-start"
                    spacing={1}
                    sx={{ width: '100%' }}
                  >
                    <Typography component="div" fontWeight={600} sx={{ flexGrow: 1, minWidth: 0 }}>
                      {student.canonicalSaintName ? `${student.canonicalSaintName} ` : ''}
                      {student.fullName}
                    </Typography>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={0.5}
                      sx={{ flexShrink: 0 }}
                    >
                      {statusLabel(student)}
                      {clickable && (
                        <ChevronRightIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                      )}
                    </Stack>
                  </Stack>
                }
                secondary={
                  <Stack spacing={1} mt={1}>
                    {lastYearSummary(student, config?.onlineEnrollmentOpen)}
                    {ineligibleText && (
                      <Alert
                        severity={
                          student.ineligibleCode === 'COMPLETED_PROGRAM' ? 'success' : 'info'
                        }
                        sx={{ py: 0 }}
                      >
                        {ineligibleText}
                      </Alert>
                    )}
                    {student.pendingPaymentCode && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={(e) => {
                          e.stopPropagation()
                          navigate(`/ghi-danh?code=${student.pendingPaymentCode}`)
                        }}
                      >
                        Xem đơn {student.pendingPaymentCode}
                      </Button>
                    )}
                  </Stack>
                }
              />
            )

            return (
              <ListItem
                key={student.studentId}
                disablePadding
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  mb: 2,
                }}
              >
                {clickable ? (
                  <ListItemButton
                    sx={{ p: 2, flexDirection: 'column', alignItems: 'flex-start' }}
                    onClick={() => navigate(`/student/${student.studentId}`)}
                  >
                    {itemContent}
                  </ListItemButton>
                ) : (
                  <Box sx={{ p: 2, width: '100%' }}>{itemContent}</Box>
                )}
              </ListItem>
            )
          })}
        </List>

        {showEnrollButton && (
          <Button variant="contained" size="large" onClick={() => navigate('/ghi-danh')}>
            Ghi danh
          </Button>
        )}
      </Stack>
    </Container>
  )
}

export default GuardianHome
